/* ──────────────────────────────────────────────────────────────────────────
   ConfigurationCompetencia.js
   Pantalla principal de configuración de una competencia
   Sincroniza la pestaña visible con la URL :tab y viceversa
   (Breadcrumbs removidos)
   ────────────────────────────────────────────────────────────────────────── */

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Alert } from '@mui/material';

import CompetitionTabs    from '../../components/gestionEvento/CompetitionTabs';
import SigertreLoader     from '../../components/Common/SigertreLoader';
import { useCompetencia } from '../../context/CompetenciaContext';

/* Mapas entre slug <-> índice (sincronizados con CompetitionTabs) */
const tabNameToIndex = {
  ajustes:        0,
  convocatoria:   1,
  pruebas:        2,
  competidores:   3,
  equipos:        4,
  horarios:       5,
  siembra:        6,
  jueces:         7,
  numeracion:     8,
  records:        9,
  integraciones: 10,
  notificaciones:11,
  vistaprevia:   12
};
const indexToTabName = Object.keys(tabNameToIndex);

export default function ConfigurationCompetencia() {
  const { competitionId, tab = 'ajustes' } = useParams();
  const navigate = useNavigate();

  const {
    competencias,
    updateCompetencia,
    deleteCompetencia,
    loading: loadingCompetencias
  } = useCompetencia();

  const competencia = (!loadingCompetencias && competitionId)
    ? competencias.find(c => c.id === competitionId)
    : null;

  const currentTabIndex = tabNameToIndex[tab.toLowerCase()] ?? 0;

  useEffect(() => {
    if (competitionId && tab && tabNameToIndex[tab.toLowerCase()] === undefined) {
      navigate(`/${competitionId}/ajustes`, { replace: true });
    }
  }, [competitionId, tab, navigate]);

  if (loadingCompetencias) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <SigertreLoader loaderText="SIGERTRE" message="Cargando configuración..." />
      </Box>
    );
  }

  if (!competitionId) {
    return (
      <Box sx={{ mx: 'auto', px: { xs: 0.75, sm: 1.5, md: 2 }, py: 2, maxWidth: { xs: '100%', lg: 1360, xl: 1600 } }}>
        <Alert severity="info">Por favor, selecciona una competencia para configurar.</Alert>
      </Box>
    );
  }

  if (!competencia) {
    return (
      <Box sx={{ mx: 'auto', px: { xs: 0.75, sm: 1.5, md: 2 }, py: 2, maxWidth: { xs: '100%', lg: 1360, xl: 1600 } }}>
        <Alert severity="error">
          Competencia no encontrada para el ID: <strong>{competitionId}</strong>
        </Alert>
      </Box>
    );
  }

  const handleTabChange = (newIdx, newCompetitionId = competitionId) => {
    const newSlug = indexToTabName[newIdx] || 'ajustes';
    if (newCompetitionId !== competitionId) {
      navigate(`/${newCompetitionId}/ajustes`);
      return;
    }
    if (newIdx !== currentTabIndex) {
      navigate(`/${competitionId}/${newSlug}`);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, width: '100%' }}>
      <Box
        sx={{
          mx: 'auto',
          px: { xs: 0.75, sm: 1.5, md: 2 },
          py: 2,
          maxWidth: { xs: '100%', lg: 1360, xl: 1600 },
          minWidth: 0,
        }}
      >
        <CompetitionTabs
          competitionId={competitionId}
          currentTab={currentTabIndex}
          onTabChange={handleTabChange}
          deleteCompetencia={deleteCompetencia}
          updateCompetencia={updateCompetencia}
        />
      </Box>
    </Box>
  );
}
